import React from 'react';
import clsx from 'clsx';
import {
	useFetchAutocompletePredictions,
	useFetchPlaceDetails
} from '../api/gis';
import styles from './SearchFormAddressBar.module.scss';
import Icon from './Icon';

import useAutocomplete from '@material-ui/lab/useAutocomplete';
import { useTCLog } from '../utils/TCEvent';
import { TCLogTypes, TC_EVENT_NAMES } from '../utils/Constants';

function getOptionLabel(option) {
	return option.description;
}

export default function SearchFormAddressBar({
	rootClassName,
	inputValue,
	onInputChange,
	onSubmit,
	autocompleteResults,
	setAutocompleteResult,
	openInfoWindow,
	focusOnMount,
	...rest
}) {
	const [TCLog, setTCAppData] = useTCLog();

	const [value, setValue] = React.useState(null);

	const [error, setError] = React.useState(null);
	const [options, setOptions] = React.useState([]);

	const fetchAutocompletePredictions = useFetchAutocompletePredictions();
	const fetchPlaceDetails = useFetchPlaceDetails();

	React.useEffect(() => {
		if (fetchAutocompletePredictions == null || !inputValue) {
			return;
		}

		let normalizedOptions = [];

		function normalizeAndSetOptions(placeOptions) {
			normalizeResultOptions();
			normalizePlaceOptions(placeOptions);
			setOptions(normalizedOptions);
		}

		function normalizePlaceOptions(placeOptions) {
			placeOptions.map((placeOption) => {
				const normalizedOption = {
					mainText: placeOption.structured_formatting.main_text,
					secondaryText:
						placeOption.structured_formatting.secondary_text,
					description: placeOption.description,
					icon: (
						<Icon
							icon={'pin/pin_agenzie_disable.png'}
							size="small"
							textOn="right"
						/>
					),
					place: placeOption
				};
				normalizedOptions.push(normalizedOption);
			});
		}

		function normalizeResultOptions() {
			if (autocompleteResults) {
				for (let i = 0; i < autocompleteResults.length && i < 5; i++) {
					const result = autocompleteResults[i];
					const normalizedOption = {
						mainText: result.name,
						secondaryText: result.address,
						description: result.name,
						icon: (
							<Icon
								icon={'pin/pin_agenzie.png'}
								size="small"
								textOn="right"
							/>
						),
						result: result
					};
					normalizedOptions.push(normalizedOption);
				}
			}
		}

		let ignore = false;

		fetchAutocompletePredictions({
			input: inputValue,
			type: ['geocode'],
			componentRestrictions: { country: 'it' }
		})
			.then(normalizeAndSetOptions)
			.catch(setError);

		return () => {
			ignore = true;
		};
	}, [fetchAutocompletePredictions, autocompleteResults, inputValue]);

	function handleChange(event, nextValue) {
		setValue(nextValue);

		if (!nextValue) return;

		if (nextValue.place) {
			fetchPlaceDetails({
				placeId: nextValue.place.place_id
			}).then((place) => {
				onSubmit({
					lat: place.geometry.location.lat(),
					lng: place.geometry.location.lng(),
					formattedAddress: place.formatted_address,
					place: place
				});
				TCLog(
					TC_EVENT_NAMES.SEARCH_ADDRESS,
					null,
					TCLogTypes.searchAddress,
					place.formatted_address
				);
			});
		} else if (nextValue.result) {
			// nextValue.result.showOnMap();
			// setTimeout(() => {
			// 	openInfoWindow(nextValue.result.id);
			// }, 500);
			setAutocompleteResult(nextValue.result);
		}
	}

	const {
		groupedOptions,
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps
	} = useAutocomplete({
		id: 'axa-map-search-form-address-bar',
		options,
		getOptionLabel,
		value,
		inputValue,
		onChange: handleChange,
		onInputChange,
		clearOnBlur: false
	});

	return (
		<div className={clsx(styles.root, rootClassName)} {...getRootProps()}>
			<input autoFocus={focusOnMount??false} tabIndex={0} {...rest} {...getInputProps()} />
			{groupedOptions.length > 0 ? (
				<ul className={styles.listBox} {...getListboxProps()}>
					{groupedOptions.map((option, index) => {
						const itemProps = getOptionProps({ option, index });

						return (
							<li className={styles.listBoxItem} {...itemProps}>
								{/*
								option.structured_formatting.main_text
									Type:  string
									This is the main text part of the unformatted description of the place suggested by the Places service. Usually the name of the place.
								option.structured_formatting.main_text_matched_substrings 	
									Type:  Array<PredictionSubstring>
									A set of substrings in the main text that match elements in the user's input, suitable for use in highlighting those substrings. Each substring is identified by an offset and a length, expressed in unicode characters.
								option.structured_formatting.secondary_text 	
									Type:  string
									This is the secondary text part of the unformatted description of the place suggested by the Places service. Usually the location of the place.
								*/}
								{option.icon}
								<strong>{option.mainText}</strong>{' '}
								<small>{option.secondaryText}</small>
							</li>
						);
					})}
				</ul>
			) : null}
		</div>
	);
}
